import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { UKRAINE } from 'constants/contacts'
import { useYupFields } from 'hooks/forms/useYupFields'
import { ContactEntity, ContactEntityType } from 'types/contacts'

export type ContactFormData = {
  entityType: ContactEntityType
  companyName: string
  contactDescription: string
  companyRegistrationNumber: string
  companyRegistrationCountry: string
  addressLine1: string
  addressLine2: string
  city: string
  postCode: string
  region: string
  regionCode: string
  firstName: string
  lastName: string
  countryOfResidence: string
  dateOfBirth: string
  isSelfOwned: boolean
}

export type ContactFormKeys = keyof ContactFormData

export const contactSchema = (t: TFunction<'translation', undefined>) => {
  const { getRequiredYupField, getConditionalYupField, getYupField, getCheckboxYupField } =
    useYupFields()

  return yup.object<Record<ContactFormKeys, yup.AnySchema>>({
    entityType: yup.string().required(),
    companyName: getConditionalYupField({
      label: t('formFields.companyName'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.COMPANY,
    }),
    contactDescription: getRequiredYupField({
      label: t('formFields.contactDescription'),
    }),
    companyRegistrationNumber: getConditionalYupField({
      label: t('formFields.companyRegistrationNumber'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.COMPANY,
    }),
    companyRegistrationCountry: getConditionalYupField({
      label: t('formFields.companyRegistrationCountry'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.COMPANY,
    }),
    addressLine1: getRequiredYupField({
      label: t('formFields.addressLine1'),
    }),
    addressLine2: getYupField({
      label: `${t('formFields.addressLine2')} (${t('optional')})`,
    }),
    city: getRequiredYupField({
      label: t('formFields.city'),
    }),
    postCode: getRequiredYupField({
      label: t('formFields.postCode'),
      requiredErrorMessage: t('formFields.required.postCode'),
    }),
    region: getYupField({
      label: `${t('formFields.region')} (${t('optional')})`,
    }),
    regionCode: yup
      .string()
      .when(['companyRegistrationCountry', 'countryOfResidence', 'entityType'], {
        is: (companyCountry: string, residenceCountry: string, entityType: string) =>
          (entityType === ContactEntity.COMPANY && companyCountry === UKRAINE) ||
          (entityType === ContactEntity.INDIVIDUAL && residenceCountry === UKRAINE),
        then: getRequiredYupField({
          label: t('formFields.region'),
          requiredErrorMessage: t('formFields.required.region'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    firstName: getConditionalYupField({
      label: t('formFields.firstName'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.INDIVIDUAL,
    }),
    lastName: getConditionalYupField({
      label: t('formFields.lastName'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.INDIVIDUAL,
    }),
    countryOfResidence: getConditionalYupField({
      label: t('formFields.countryOfResidence'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.INDIVIDUAL,
    }),
    dateOfBirth: getConditionalYupField({
      label: t('formFields.dateOfBirth'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.INDIVIDUAL,
    }),
    isSelfOwned: getCheckboxYupField(t('formFields.myCompany')),
  })
}
